import React, { useEffect, useState } from "react";
import PatientRegistration from "../../components/patient/PatientRegistration";
import { useNavigate, useParams } from "react-router-dom";
import {
  getPatientBasicDetails,
  registerPortalDetails,
  verifyOtpDetails,
} from "../../apis/patientService";
import { errorMessage } from "../../utils/notificationUtils";
import { Row, Spin, Typography } from "antd";
import { Col } from "antd";
import {
  CreditCardOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Form } from "antd";
import { registerPatientPortalCghsNew } from "../../apis/SchemeService/CGHS/cghsService";
import PortalRegistration from "../../components/patient/PortalRegistration";
import dayjs from "dayjs";

const { Text } = Typography;

const PatientRegistrationContainer = () => {
  const { visitId } = useParams();
  const [visitDetails, setVisitDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOtpVerified, setIsOtpVerified] = useState<boolean>(false);
  const [isMobileVerified, setIsMobileVerified] = useState<boolean>(false);
  const navigate = useNavigate();
  const [isError, setIsError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isOtpVerifying, setIsOtpVerifying] = useState<boolean>(false);
  const [isMobileVerifying, setIsMobileVerifying] = useState<boolean>(false);

  const fetchVisitDetails = async () => {
    try {
      setIsLoading(true);
      const res = await getPatientBasicDetails(visitId);
      setVisitDetails(res?.fetchBasicDetail);
    } catch (err) {
      errorMessage("Please, Try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (visitId) {
      fetchVisitDetails();
    }
  }, [visitId]);

  const [form] = Form.useForm();

  const handleVerifyMobile = async () => {
    try {
      await form.validateFields(["mobileNumber"]);
      const payload = {
        mobileNumber: form.getFieldValue("mobileNumber"),
        otp: null,
        otpProvided: false,
        hospitalId: visitDetails?.casesResponse?.healthCareId,
        schemeId: visitDetails?.casesResponse?.schemeMetaDataId,
      };

      setIsMobileVerifying(true);
      const res = await verifyOtpDetails(
        visitDetails?.casesResponse?.id,
        visitDetails?.visitInfoResponse?.id,
        payload,
      );
      setIsMobileVerified(true);
      setIsError(false);
    } catch (err: any) {
      if (err?.errorFields) {
      } else {
        setIsError(true);
        setIsMobileVerified(false);
      }
    } finally {
      setIsMobileVerifying(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      await form.validateFields(["mobileNumber", "otp"]);
      const payload = {
        mobileNumber: form.getFieldValue("mobileNumber"),
        otp: form.getFieldValue("otp"),
        otpProvided: true,
        hospitalId: visitDetails?.casesResponse?.healthCareId,
        schemeId: visitDetails?.casesResponse?.schemeMetaDataId,
      };

      setIsOtpVerifying(true);
      const res = await verifyOtpDetails(
        visitDetails?.casesResponse?.id,
        visitDetails?.visitInfoResponse?.id,
        payload,
      );
      setIsOtpVerified(true);
      setIsError(false);
    } catch (err: any) {
      if (err?.errorFields) {
      } else {
        setIsError(true);
        setIsOtpVerified(false);
      }
    } finally {
      setIsOtpVerifying(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = await form.getFieldsValue(true);
      const payload = {
        communicationAddress: {
          sameAsCardAddress: true,
          pincode: values?.pincode,
          city: values?.city,
        },
        patientAttendantDetails: {
          attendantType: values?.attendant?.toUpperCase(),
          fullName:
            values?.attendant === "self"
              ? visitDetails?.patientResponse?.fullName
              : values?.fullName,
          relationship: values?.relationship,
          otherRelation: values?.otherRelation,
          mobileNumber: values?.mobileNumber,
          otp: values?.otp,
          otpProvided: true,
          beneficiaryOwnedMobile: true,
        },
        hospitalId: visitDetails?.casesResponse?.healthCareId,
        schemeId: visitDetails?.casesResponse?.schemeMetaDataId,
      };
      setIsSubmitting(true);
      const res = await registerPortalDetails(
        visitDetails?.casesResponse?.id,
        visitDetails?.visitInfoResponse?.id,
        payload,
      );
      navigate(
          `/app/claim-id-generation/${visitDetails?.visitInfoResponse?.id}`,
        );
      setIsError(false);
    } catch (err: any) {
      if (err?.errorFields) {
      } else {
        setIsError(true);
      }
    }
    finally {
        setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (visitDetails && isError) {
      form.setFieldsValue({
        beneficiaryId:
          visitDetails?.casesResponse?.memberId ||
          visitDetails?.referralInfoResponse?.cghsReferralResponse
            ?.beneficiaryId,
        registrationDate: visitDetails?.visitInfoResponse?.doa
          ? dayjs(visitDetails?.visitInfoResponse?.doa, "DD-MM-YYYY HH:mm:ss")
          : null,
      });
    }
  }, [visitDetails, isError]);

  const handlePortalRegistration = async () => {
    try {
      await form.validateFields();
      const values = await form.getFieldsValue(true);
      if (visitDetails?.casesResponse?.schemeMetaDataId) {
        const updatedData = {
          beneficiaryId:
            visitDetails?.casesResponse?.memberId ||
            visitDetails?.referralInfoResponse?.cghsReferralResponse
              ?.beneficiaryId,
          registrationDate: values?.registrationDate
            ? dayjs(values?.registrationDate).format("DD-MM-YYYY HH:mm:ss")
            : null,
          manualRegistration: true,
          schemeRegistrationId: values?.registrationId,
          caseId: visitDetails?.casesResponse?.id,
          visitId: visitDetails?.visitInfoResponse?.id,
          isCghs:
            visitDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
            "CGHS NEW"
              ? true
              : false,
          hospitalId: visitDetails?.casesResponse?.healthCareId,
          schemeId: visitDetails?.casesResponse?.schemeMetaDataId,
          integratedWithPortal:
            visitDetails?.casesResponse?.integratedWithPortal,
        };
        setIsSubmitting(true);
        const res = await registerPatientPortalCghsNew(
          visitDetails?.casesResponse?.portalMetaDataIds?.[0],
          updatedData,
        );
        if (res) {
          navigate(
            `/app/claim-id-generation/${visitDetails?.visitInfoResponse?.id}`,
          );
          // successMessage("Registered Successfully");
        }
      }
    } catch (err) {
      // errorMessage("Please, Try again later");
    }
    finally {
        setIsSubmitting(false);
    }
  };

  return (
    <Spin size="small" spinning={isLoading}>
      <div
        style={{
          padding: "10px 12px",
          background: "#F1F8FF",
          //   borderBottom: "1px solid #eee",
        }}
      >
        <Row justify="space-between">
          <Col>
            <div style={{ fontWeight: 600 }}>
              {visitDetails?.patientResponse?.fullName}
            </div>
            <Text type="secondary">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  marginTop: "0.6rem",
                }}
              >
                <CreditCardOutlined
                  style={{ fontSize: 12, color: "#717182" }}
                />
                <span>{`${visitDetails?.casesResponse?.schemeMetaDataName}-${visitDetails?.visitInfoResponse?.creditNumber}`}</span>
              </div>
            </Text>
          </Col>

          <Col>
            <div
              style={{
                color: "#717182",
                fontSize: "12px",
              }}
            >
              {visitDetails?.patientResponse?.age} yrs,{" "}
              {visitDetails?.patientResponse?.sex}
            </div>
          </Col>
        </Row>

        {/* <Text type="secondary">📍 New Delhi, Delhi</Text> */}
        {isError ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "6px 10px",
              border: "1px solid #f5c2c7",
              backgroundColor: "#f8d7da",
              borderRadius: "5px",
              color: "#842029",
              fontSize: "10px",
              maxWidth: "600px",
              margin: "10px 0",
            }}
          >
            <ExclamationCircleOutlined
              style={{
                fontSize: 10,
                color: "#dc3545",
              }}
            />
            <span style={{ fontSize: "8px", color: "#D4183D" }}>
              There is an error. Please, Proceed Manually.
            </span>
          </div>
        ) : (
          <></>
        )}
      </div>

      {isError ? (
        <PortalRegistration
          form={form}
          handleSubmit={handlePortalRegistration}
          isSubmitting={isSubmitting}
        />
      ) : (
        <PatientRegistration
          form={form}
          handleVerifyMobile={handleVerifyMobile}
          handleVerifyOtp={handleVerifyOtp}
          isOtpVerified={isOtpVerified}
          isMobileVerified={isMobileVerified}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          isOtpVerifying={isOtpVerifying}
          isMobileVerifying={isMobileVerifying}
        />
      )}
    </Spin>
  );
};

export default PatientRegistrationContainer;
